import { inject, Injectable, signal } from '@angular/core';
import { APIService, Invite, CreateInviteInput, UpdateInviteInput, ModelInviteFilterInput } from './api';
import { NgxSpinnerService } from 'ngx-spinner';
import { v4 as uuidv4 } from 'uuid';
import { environment } from 'src/environments/environment';
import { EmailService } from './email.service';

// Status types for invite tracking: INVITED → REGISTERED → STARTED → COMPLETED
export type InviteStatus = 'invited' | 'registered' | 'started' | 'completed';

// Backward compat: treat legacy 'accepted' as 'registered'
export function normalizeStatus(status: string): InviteStatus {
  if (status === 'accepted') return 'registered';
  return status as InviteStatus;
}

// Interface matching DynamoDB Invite table structure
export interface InviteRecord {
  id: string;
  userId: string;           // Inviter User ID (from UserDetails table)
  companyId: string;        // Inviter Company ID (from Company table)
  inviteeEmail: string;     // Email entered in invite popup
  inviterEmail?: string;    // Inviter's email (from Company contactEmail)
  associatedCompanies?: string[]; // Multiple company IDs selected by invitee when accepting
  status: InviteStatus;
  statusUpdatedAt?: string; // DateTime when status changed
  createdAt: string;        // DateTime when invite was created
  updatedAt: string;        // DateTime when invite was last updated
}

// For display in the linked companies table
export interface LinkedCompanyDisplay {
  id: string;
  inviteId: string;
  companyId: string;
  companyName: string;
  companyNames: string[]; // Array of associated company names for multi-line display
  email: string;
  status: InviteStatus;
  createdAt: string;
  statusUpdatedAt?: string;
  linkedAt?: string; // Alias for createdAt for template compatibility
}

@Injectable({
  providedIn: 'root'
})
export class InviteService {
  private api = inject(APIService);
  private spinner = inject(NgxSpinnerService);
  private emailService = inject(EmailService);

  invites = signal<InviteRecord[]>([]);

  constructor() { }

  // Load invites for a specific user (inviter)
  async loadInvitesForUser(userId: string): Promise<void> {
    try {
      const result = await this.api.InvitesByUserId(userId, undefined, undefined, 10000);
      const inviteRecords: InviteRecord[] = (result?.items || [])
        .filter((item): item is Invite => item !== null)
        .map(item => ({
          id: item.id,
          userId: item.userId,
          companyId: item.companyId,
          inviteeEmail: item.inviteeEmail,
          inviterEmail: item.inviterEmail || undefined,
          associatedCompanies: item.associatedCompanies?.filter((c): c is string => c !== null) || [],
          status: normalizeStatus(item.status),
          statusUpdatedAt: item.statusUpdatedAt || undefined,
          createdAt: item.createdAt,
          updatedAt: item.updatedAt
        }));
      this.invites.set(inviteRecords);
    } catch (error) {
      console.error('Error loading invites:', error);
      this.invites.set([]);
    }
  }

  // Load invites for a specific company (inviter's company)
  async loadInvitesForCompany(companyId: string): Promise<InviteRecord[]> {
    try {
      // Use listInvites with filter instead of GSI query
      const filter: ModelInviteFilterInput = {
        companyId: { eq: companyId }
      };
      const result = await this.api.ListInvites(filter, 10000);
      return (result?.items || [])
        .filter((item): item is Invite => item !== null)
        .map(item => ({
          id: item.id,
          userId: item.userId,
          companyId: item.companyId,
          inviteeEmail: item.inviteeEmail,
          inviterEmail: item.inviterEmail || undefined,
          associatedCompanies: item.associatedCompanies?.filter((c): c is string => c !== null) || [],
          status: normalizeStatus(item.status),
          statusUpdatedAt: item.statusUpdatedAt || undefined,
          createdAt: item.createdAt,
          updatedAt: item.updatedAt
        }));
    } catch (error) {
      console.error('Error loading invites for company:', error);
      return [];
    }
  }

  // Load invites sent to a specific email (invitee)
  // Scans all invites and filters by case-insensitive email match
  // This handles legacy records with non-lowercase emails
  async loadInvitesForEmail(email: string): Promise<InviteRecord[]> {
    try {
      const normalizedEmail = email.trim().toLowerCase();

      // Scan all invites and filter by case-insensitive email match
      // This ensures we find invites regardless of how the email was stored
      const result = await this.api.ListInvites(undefined, 10000);
      const items = (result?.items || [])
        .filter((item): item is Invite => item !== null)
        .filter(item => item.inviteeEmail.toLowerCase() === normalizedEmail);

      return items.map(item => ({
        id: item.id,
        userId: item.userId,
        companyId: item.companyId,
        inviteeEmail: item.inviteeEmail,
        inviterEmail: item.inviterEmail || undefined,
        associatedCompanies: item.associatedCompanies?.filter((c): c is string => c !== null) || [],
        status: normalizeStatus(item.status),
        statusUpdatedAt: item.statusUpdatedAt || undefined,
        createdAt: item.createdAt,
        updatedAt: item.updatedAt
      }));
    } catch (error) {
      console.error('Error loading invites for email:', error);
      return [];
    }
  }

  // Check if an invite already exists for the given email and company
  async checkExistingInvite(inviteeEmail: string): Promise<InviteRecord | null> {
    try {
      const normalizedEmail = inviteeEmail.trim().toLowerCase();

      const filter: ModelInviteFilterInput = {
        inviteeEmail: { eq: normalizedEmail }
      };

      const result = await this.api.ListInvites(filter, 100);

      const invites = (result?.items || [])
        .filter((item): item is Invite => item !== null);

      const existingInvite = invites[0];

      console.log(
        'Checking existing invite for',
        normalizedEmail,
        ':',
        existingInvite ? 'FOUND' : 'NOT FOUND'
      );

      if (existingInvite) {
        return {
          id: existingInvite.id,
          userId: existingInvite.userId,
          companyId: existingInvite.companyId,
          inviteeEmail: existingInvite.inviteeEmail,
          inviterEmail: existingInvite.inviterEmail || undefined,
          associatedCompanies:
            existingInvite.associatedCompanies?.filter(
              (c): c is string => c !== null
            ) || [],
          status: normalizeStatus(existingInvite.status),
          statusUpdatedAt: existingInvite.statusUpdatedAt || undefined,
          createdAt: existingInvite.createdAt,
          updatedAt: existingInvite.updatedAt
        };
      }

      return null;
    } catch (error) {
      console.error('Error checking existing invite:', error);
      throw error;
    }
  }

  // Create a new invite entry in DynamoDB
  async createInvite(userId: string, companyId: string, inviteeEmail: string, inviterEmail: string): Promise<InviteRecord | null> {
    try {
      this.spinner.show();

      // Normalize email to lowercase to prevent case sensitivity issues
      const normalizedInviteeEmail = inviteeEmail.trim().toLowerCase();

      // Check for existing invite with same email for this company
      const existingInvite = await this.checkExistingInvite(normalizedInviteeEmail);
      if (existingInvite) {
        throw new Error('DUPLICATE_INVITE');
      }

      const input: CreateInviteInput = {
        id: uuidv4(),
        userId,
        companyId,
        inviteeEmail: normalizedInviteeEmail,
        inviterEmail,
        status: 'invited',
        associatedCompanies: []
      };

      const result = await this.api.CreateInvite(input);

      const newInvite: InviteRecord = {
        id: result.id,
        userId: result.userId,
        companyId: result.companyId,
        inviteeEmail: result.inviteeEmail,
        inviterEmail: result.inviterEmail || undefined,
        associatedCompanies: result.associatedCompanies?.filter((c): c is string => c !== null) || [],
        status: normalizeStatus(result.status),
        statusUpdatedAt: result.statusUpdatedAt || undefined,
        createdAt: result.createdAt,
        updatedAt: result.updatedAt
      };

      // Update local signal
      const currentInvites = this.invites();
      this.invites.set([...currentInvites, newInvite]);

      return newInvite;
    } catch (error: any) {
      console.error('Error creating invite:', error);
      // Re-throw DUPLICATE_INVITE error so the UI can show the proper message
      if (error?.message === 'DUPLICATE_INVITE') {
        throw error;
      }
      return null;
    } finally {
      this.spinner.hide();
    }
  }

  // Create and immediately accept an invite (for direct link sharing scenario)
  // This is used when a user accesses an invite link that was shared externally (not via email)
  async createAndAcceptInvite(
    userId: string,
    companyId: string,
    inviteeEmail: string,
    associatedCompanyIds: string[]
  ): Promise<InviteRecord | null> {
    try {
      this.spinner.show();

      // Normalize email to lowercase
      const normalizedInviteeEmail = inviteeEmail.trim().toLowerCase();

      // Create the invite with 'registered' status directly
      const input: CreateInviteInput = {
        id: uuidv4(),
        userId,
        companyId,
        inviteeEmail: normalizedInviteeEmail,
        inviterEmail: '', // No inviter email for direct link scenario
        status: 'registered',
        associatedCompanies: associatedCompanyIds,
        statusUpdatedAt: new Date().toISOString()
      };

      const result = await this.api.CreateInvite(input);

      const newInvite: InviteRecord = {
        id: result.id,
        userId: result.userId,
        companyId: result.companyId,
        inviteeEmail: result.inviteeEmail,
        inviterEmail: result.inviterEmail || undefined,
        associatedCompanies: result.associatedCompanies?.filter((c): c is string => c !== null) || [],
        status: normalizeStatus(result.status),
        statusUpdatedAt: result.statusUpdatedAt || undefined,
        createdAt: result.createdAt,
        updatedAt: result.updatedAt
      };

      // Update local signal
      const currentInvites = this.invites();
      this.invites.set([...currentInvites, newInvite]);

      // Note: No notification email is sent for direct link acceptance
      // since there was no formal invitation sent

      return newInvite;
    } catch (error) {
      console.error('Error creating and accepting invite:', error);
      return null;
    } finally {
      this.spinner.hide();
    }
  }

  // Accept an invite - update status and associated companies
  async acceptInvite(inviteId: string, associatedCompanyIds: string[], inviterEmail?: string, inviterCompanyName?: string, inviteeEmail?: string): Promise<boolean> {
    try {
      this.spinner.show();

      const input: UpdateInviteInput = {
        id: inviteId,
        status: 'registered',
        associatedCompanies: associatedCompanyIds,
        statusUpdatedAt: new Date().toISOString()
      };

      await this.api.UpdateInvite(input);

      // Update local signal
      const currentInvites = this.invites();
      const index = currentInvites.findIndex(i => i.id === inviteId);
      if (index !== -1) {
        currentInvites[index] = {
          ...currentInvites[index],
          status: 'registered',
          associatedCompanies: associatedCompanyIds,
          statusUpdatedAt: new Date().toISOString()
        };
        this.invites.set([...currentInvites]);
      }

      // Send notification email to inviter
      if (inviterEmail && inviterCompanyName && inviteeEmail) {
        try {
          await this.emailService.sendInviteAcceptedEmail(inviterEmail, {
            companyName: inviterCompanyName,
            inviteeEmail: inviteeEmail
          });
        } catch (emailError) {
          console.error('Failed to send invite accepted email:', emailError);
        }
      }

      return true;
    } catch (error) {
      console.error('Error accepting invite:', error);
      return false;
    } finally {
      this.spinner.hide();
    }
  }

  // Delete an invite
  async deleteInvite(inviteId: string): Promise<boolean> {
    try {
      this.spinner.show();

      await this.api.DeleteInvite({ id: inviteId });

      // Update local signal
      const currentInvites = this.invites();
      this.invites.set(currentInvites.filter(i => i.id !== inviteId));

      return true;
    } catch (error) {
      console.error('Error deleting invite:', error);
      return false;
    } finally {
      this.spinner.hide();
    }
  }

  // Get invite by ID
  async getInviteById(inviteId: string): Promise<InviteRecord | null> {
    try {
      const result = await this.api.GetInvite(inviteId);
      if (!result) return null;

      return {
        id: result.id,
        userId: result.userId,
        companyId: result.companyId,
        inviteeEmail: result.inviteeEmail,
        inviterEmail: result.inviterEmail || undefined,
        associatedCompanies: result.associatedCompanies?.filter((c): c is string => c !== null) || [],
        status: normalizeStatus(result.status),
        statusUpdatedAt: result.statusUpdatedAt || undefined,
        createdAt: result.createdAt,
        updatedAt: result.updatedAt
      };
    } catch (error) {
      console.error('Error getting invite:', error);
      return null;
    }
  }

  // Generate invite link (company ID is used as the permanent link token)
  // Company name is embedded as a query param so the invite page needs no unauthenticated GraphQL call
  generateInviteLink(companyId: string, companyName: string = ''): string {
    const base = `${environment.baseUrl}/invite/${companyId}`;
    return companyName
      ? `${base}?company=${encodeURIComponent(companyName)}`
      : base;
  }

  // Get invites for display in the stakeholder engagement table
  getLinkedCompaniesDisplay(
    companyNamesMap?: Map<string, string>
  ): LinkedCompanyDisplay[] {
    return this.invites().map(invite => {
      const companyNamesArray: string[] = [];

      if (invite.associatedCompanies && invite.associatedCompanies.length > 0) {
        invite.associatedCompanies.forEach(id => {
          const name = companyNamesMap?.get(id);
          if (name) {
            companyNamesArray.push(name);
          }
        });
      }

      return {
        id: uuidv4(),
        inviteId: invite.id,
        companyId: invite.companyId,
        companyName: companyNamesArray.join(', ') || '',
        companyNames: companyNamesArray,
        email: invite.inviteeEmail,
        status: invite.status,
        createdAt: invite.createdAt,
        statusUpdatedAt: invite.statusUpdatedAt,
        linkedAt: invite.createdAt
      };
    });
  }

  // Get all unique associated company IDs from current invites
  getAssociatedCompanyIds(): string[] {
    const ids = new Set<string>();
    this.invites().forEach(invite => {
      if (invite.associatedCompanies) {
        invite.associatedCompanies.forEach(id => ids.add(id));
      }
    });
    return Array.from(ids);
  }

  // Fetch company name by ID from API
  async fetchCompanyName(companyId: string): Promise<string | null> {
    try {
      const result = await this.api.GetCompany(companyId);
      return result?.companyName || null;
    } catch (error) {
      console.error('Error fetching company name:', error);
      return null;
    }
  }

  // Update invite status (for status progression: invited → registered → started → completed)
  async updateInviteStatus(inviteId: string, newStatus: InviteStatus): Promise<boolean> {
    try {
      const input: UpdateInviteInput = {
        id: inviteId,
        status: newStatus,
        statusUpdatedAt: new Date().toISOString()
      };

      await this.api.UpdateInvite(input);

      // Update local signal
      const currentInvites = this.invites();
      const index = currentInvites.findIndex(i => i.id === inviteId);
      if (index !== -1) {
        currentInvites[index] = {
          ...currentInvites[index],
          status: newStatus,
          statusUpdatedAt: new Date().toISOString()
        };
        this.invites.set([...currentInvites]);
      }

      return true;
    } catch (error) {
      console.error('Error updating invite status:', error);
      return false;
    }
  }

  // Load invites for a specific company (for display in the emission page)
  async loadInvitesForCompanyDisplay(companyId: string): Promise<void> {
    try {
      const invites = await this.loadInvitesForCompany(companyId);
      this.invites.set(invites);
    } catch (error) {
      console.error('Error loading invites for company display:', error);
      this.invites.set([]);
    }
  }

  // Check if an email belongs to a registered user by querying Cognito user pool via Lambda
  async isRegisteredUser(email: string): Promise<boolean> {
    return this.emailService.checkUserExists(email);
  }

  // Send invitation email via Lambda/SES
  async sendInvitationEmail(inviteeEmail: string, inviterCompanyName: string, inviteLink: string): Promise<void> {
    await this.emailService.sendInviteEmail(inviteeEmail, {
      companyName: inviterCompanyName,
      inviteLink: inviteLink
    });
  }
}
